import { Component, OnInit } from '@angular/core'
import { BackendService } from 'src/app/services/backend.service'
import { environment } from 'src/environments/environment'
import Swal from 'sweetalert2'

interface TestFunction {
    name: string
    endpoint: string
    description: string
    category: string
    schedule?: string
    frequency?: string
    isRunning?: boolean
}

@Component({
    selector: 'app-admin-test-local',
    templateUrl: './admin-test-local.component.html',
    styleUrls: ['./admin-test-local.component.scss'],
})
export class AdminTestLocalComponent implements OnInit {
    isEmulator = environment.useEmulators

    testFunctions: TestFunction[] = [
        // CRM
        {
            name: 'Full Subscription Data Sync',
            endpoint: 'syncAdminClientsSubscriptionData',
            description: 'Rebuild subscriptionSummaries and CRM fields for all admin clients from source of truth',
            category: 'CRM',
            schedule: 'Every day at 04:00',
            frequency: 'Daily',
        },
        {
            name: 'Close Old Leads (Pre-2026)',
            endpoint: 'migrateCloseOldLeads',
            description: 'Close all CRM leads created before 2026 that are still open',
            category: 'CRM',
            schedule: 'Manual only',
            frequency: 'Manual',
        },
        {
            name: 'Gravatar Avatar Sync',
            endpoint: 'testSyncGravatarAvatars',
            description: 'Fetch Gravatar profile images for CRM leads using their email addresses',
            category: 'CRM',
            schedule: 'Every hour',
            frequency: 'Hourly',
        },

        // Email Reports
        {
            name: 'Merchant Rejections Report',
            endpoint: 'testRoutes-testMerchantRejectionsReport',
            description: 'Send weekly merchant rejections email report',
            category: 'Email Reports',
            schedule: 'Every Friday at 07:10',
            frequency: 'Weekly',
        },
        {
            name: 'ParkUpp Daily Expiring Contracts Report',
            endpoint: 'testRoutes-testSubscriptionExpirationReport',
            description: 'Send daily expiring contracts email report',
            category: 'Email Reports',
            schedule: 'Every day at 07:00',
            frequency: 'Daily',
        },
        {
            name: 'ParkUpp Daily Email Leads',
            endpoint: 'testRoutes-testDailyEmailLeadsReport',
            description: 'Send daily email leads report',
            category: 'Email Reports',
            schedule: 'Every day at 07:05',
            frequency: 'Daily',
        },
        {
            name: 'Parker Aging Invoices Report',
            endpoint: 'testRoutes-testDailyAgingInvoicesReport',
            description: 'Send daily aging invoices report',
            category: 'Email Reports',
            schedule: 'Every day at 07:15',
            frequency: 'Daily',
        },
        {
            name: 'WhatsApp No Parking Users',
            endpoint: 'testWhatsappNoParkingUsers',
            description: 'Send WhatsApp messages to users who signed up yesterday without subscriptions',
            category: 'Email Reports',
            schedule: 'Every day at 10:00',
            frequency: 'Daily',
        },

        // Invoices
        {
            name: 'Generate Invoices',
            endpoint: 'testCronGenerateInvoices',
            description: 'Generate invoices for active subscriptions',
            category: 'Invoices',
            schedule: 'Every day at 06:00',
            frequency: 'Daily',
        },
        {
            name: 'Process Invoice Payments',
            endpoint: 'testCronProcessInvoicePayments',
            description: 'Process all pending invoice payments',
            category: 'Invoices',
            schedule: 'Every day at 17:10',
            frequency: 'Daily',
        },
        {
            name: 'Check Due Invoices',
            endpoint: 'testCronCheckDueInvoices',
            description: 'Check and process overdue invoices',
            category: 'Invoices',
            schedule: 'Every day at 07:00',
            frequency: 'Daily',
        },
        {
            name: 'Update Merchant Response Time',
            endpoint: 'testCronUpdateMerchantResponseTime',
            description: 'Calculate and update merchant average response times',
            category: 'Invoices',
            schedule: 'Every day at 08:00',
            frequency: 'Daily',
        },

        // Subscriptions
        {
            name: 'Subscription Ending Reminder',
            endpoint: 'testRoutes-testSubscriptionEndingReminder',
            description: 'Send renewal reminders for subscriptions ending in 1-12 days',
            category: 'Subscriptions',
            schedule: 'Every day at 11:30',
            frequency: 'Daily',
        },
        {
            name: 'Expire Old Subscriptions',
            endpoint: 'schedulers-testCronSubscriptionExpired',
            description: 'Find and expire subscriptions with paymentInitiated status older than 24 hours',
            category: 'Subscriptions',
            schedule: 'Every hour',
            frequency: 'Hourly',
        },

        // Data Sync
        {
            name: 'Clone Clients to Admin Clients',
            endpoint: 'testCloneClientsToAdminClients',
            description: 'Sync client data to admin clients table',
            category: 'Data Sync',
            schedule: 'Manual only',
            frequency: 'Manual',
        },
        {
            name: 'Clone Merchants to Private',
            endpoint: 'testCloneMerchantsToMerchantsPrivate',
            description: 'Sync merchant data to private merchants table',
            category: 'Data Sync',
            schedule: 'Every day at 05:00',
            frequency: 'Daily',
        },
        {
            name: 'Save Dashboard Data',
            endpoint: 'testSaveDashboardData',
            description: 'Aggregate and save dashboard statistics',
            category: 'Data Sync',
            schedule: 'Every day at 23:30',
            frequency: 'Daily',
        },

        // Testing
        {
            name: 'Test Merchant Members',
            endpoint: 'testing-testMerchantMembers',
            description: 'Retrieve merchant member user details by role',
            category: 'Testing',
            schedule: 'Manual only',
            frequency: 'Manual',
        },
        {
            name: 'Test Occupants',
            endpoint: 'testing-testOccupants',
            description: 'Get occupants for a test client',
            category: 'Testing',
            schedule: 'Manual only',
            frequency: 'Manual',
        },
        {
            name: 'Query Rejections',
            endpoint: 'testQueryRejections',
            description: 'Query all rejected and expired subscriptions',
            category: 'Testing',
            schedule: 'Manual only',
            frequency: 'Manual',
        },
    ]

    categories: string[] = []
    selectedCategory: string = 'All'

    constructor(private backendService: BackendService) {}

    ngOnInit(): void {
        const categorySet = new Set(this.testFunctions.map((fn) => fn.category))
        this.categories = ['All', ...Array.from(categorySet).sort()]
    }

    private frequencyOrder: Record<string, number> = {
        Hourly: 0,
        Daily: 1,
        Weekly: 2,
        Monthly: 3,
        Manual: 4,
    }

    get filteredFunctions(): TestFunction[] {
        const fns = this.selectedCategory === 'All' ? this.testFunctions : this.testFunctions.filter((fn) => fn.category === this.selectedCategory)
        return [...fns].sort((a, b) => {
            const aOrder = this.frequencyOrder[a.frequency || 'Manual'] ?? 5
            const bOrder = this.frequencyOrder[b.frequency || 'Manual'] ?? 5
            return aOrder - bOrder
        })
    }

    async runFunction(fn: TestFunction) {
        if (!this.isEmulator || fn.isRunning) return

        const confirmResult = await Swal.fire({
            title: `Run ${fn.name}?`,
            text: fn.description,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, run it',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        fn.isRunning = true

        try {
            const response = await this.backendService.callFunction(fn.endpoint, {})

            await Swal.fire({
                title: 'Success!',
                html: `<strong>${fn.name}</strong> executed successfully.<br><br><pre style="text-align: left; font-size: 12px;">${JSON.stringify(response, null, 2)}</pre>`,
                icon: 'success',
                confirmButtonText: 'OK',
            })
        } catch (error: any) {
            console.error(`Error running ${fn.name}:`, error)

            let errorHtml = `<strong>${fn.name}</strong> failed.<br><br>`

            if (error.message) {
                errorHtml += `<strong>Message:</strong><br><pre style="text-align: left; font-size: 12px; margin-bottom: 10px;">${error.message}</pre>`
            }

            if (error.details) {
                errorHtml += `<strong>Details:</strong><br><pre style="text-align: left; font-size: 11px; max-height: 300px; overflow-y: auto;">${JSON.stringify(error.details, null, 2)}</pre>`
            } else if (error.code) {
                errorHtml += `<strong>Code:</strong> ${error.code}<br>`
            }

            await Swal.fire({
                title: 'Error',
                html: errorHtml,
                icon: 'error',
                confirmButtonText: 'OK',
                width: '700px',
            })
        } finally {
            fn.isRunning = false
        }
    }

    getCategoryIcon(category: string): string {
        const icons: { [key: string]: string } = {
            CRM: 'fa-address-book',
            'Email Reports': 'fa-envelope',
            Invoices: 'fa-file-invoice',
            Subscriptions: 'fa-calendar-alt',
            'Data Sync': 'fa-sync',
            Testing: 'fa-vial',
        }
        return icons[category] || 'fa-cog'
    }
}
